/** How to actually sit this part of the exam.
 *
 * The client, on the listening tab: "Hoeren tab in exam is very simple ...
 * consider the instructions", pointing at a competitor screen that opens
 * with the exam's own rubric and a "how to approach this" panel.
 *
 * Two different things again, and the difference matters:
 *
 *   the rubric   — a RULE of the real exam ("you hear each text twice").
 *                  A learner who does not know it plays our audio once,
 *                  panics at what they missed, and practises the exam as
 *                  something harder than it is.
 *   the approach — technique. Read the questions first; first listen for
 *                  the gist; do not stall on one you missed.
 *
 * Folded away by default: the learner who needs it will open it, and the
 * one who has done twenty of these should not have to scroll past it every
 * time.
 */
import { useState } from "react";
import { useT } from "../../i18n";
import type { MessageKey } from "../../i18n/en";

export type ApproachKind = "hoeren" | "lesen";

const STEPS: Record<ApproachKind, readonly string[]> = {
  hoeren: ["questions", "first", "second", "guess"],
  lesen: ["skim", "locate", "trap", "guess"],
};

export function ApproachPanel({ kind = "hoeren" }: { kind?: ApproachKind }) {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-control border border-line bg-canvas">
      <p className="border-b border-line px-3 py-2 text-sm text-ink">
        {t(`approach.${kind}.rubric` as MessageKey)}
      </p>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="text-sm font-bold text-ink">{t("approach.title")}</span>
        <span aria-hidden className="text-muted">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <ol className="space-y-2 px-3 pb-3">
          {STEPS[kind].map((step, i) => (
            <li key={step} className="flex gap-2">
              <span
                aria-hidden
                className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-soft text-[11px] font-bold text-brand-dark"
              >
                {i + 1}
              </span>
              <p className="min-w-0 text-sm text-muted">
                {t(`approach.${kind}.${step}` as MessageKey)}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
